import React, { useEffect, useState } from "react";
import { search as searchApi } from "@/lib/api";
import { Search as SearchIcon, Loader2 } from "lucide-react";

export default function SearchBar({ onJump, onClose, version = "kjv" }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (q.trim().length < 2) { setResults(null); return; }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const data = await searchApi(q.trim(), version);
                setResults(data);
            } catch (_e) {
                setResults({ query: q, count: 0, results: [] });
            } finally {
                setLoading(false);
            }
        }, 220);
        return () => clearTimeout(t);
    }, [q, version]);

    return (
        <div data-testid="search-panel" className="px-5 pt-4 pb-3 border-b border-ink-10 bg-sage-paper animate-fade-in">
            <div className="relative">
                <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-40" />
                <input
                    autoFocus
                    data-testid="search-input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={`Search Matthew (${version.toUpperCase()})…`}
                    className="w-full pl-9 pr-3 py-2.5 rounded-full bg-white border border-ink-10 font-ui text-[15px] text-ink placeholder:text-ink-40 focus:outline-none focus:border-moss focus:ring-2 focus:ring-moss/20"
                />
                {loading && (
                    <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-40 animate-spin" />
                )}
            </div>
            {results && (
                <div data-testid="search-results" className="mt-3 max-h-[50vh] overflow-y-auto sheet-scroll -mx-1">
                    {results.count === 0 ? (
                        <p className="text-ink-40 font-ui text-sm px-1 py-3">No matches.</p>
                    ) : (
                        <ul className="flex flex-col gap-1.5">
                            {results.results.map((r) => (
                                <li key={`${r.chapter}-${r.verse}`}>
                                    <button
                                        data-testid={`search-result-${r.chapter}-${r.verse}`}
                                        onClick={() => { onJump(r.chapter, r.verse); onClose(); }}
                                        className="w-full text-left p-3 rounded-xl bg-white border border-ink-10 hover:border-moss/40 hover:shadow-sm transition"
                                    >
                                        <div className="font-ui text-[11px] uppercase tracking-[0.18em] text-moss-deep mb-1">
                                            Matthew {r.chapter}:{r.verse}
                                        </div>
                                        <div className="scripture text-[15px] leading-snug text-ink">
                                            {highlight(version === "web" ? (r.text_web || r.text_kjv || "") : (r.text_kjv || r.text_web || ""), q.trim())}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

function highlight(text, q) {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx < 0) return text;
    return (
        <>
            {text.slice(0, idx)}
            <mark className="bg-moss/20 text-ink px-0.5 rounded-[3px]">
                {text.slice(idx, idx + q.length)}
            </mark>
            {text.slice(idx + q.length)}
        </>
    );
}
